<script setup lang="ts">
import { cssVar, hexToRgba } from '@metapic-apps/shared'
import type { ChartData, ChartOptions, ScriptableContext, TooltipItem } from 'chart.js'
import moment from 'moment'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { Line as LineChart } from 'vue-chartjs'

import { aggregatePerformanceChart, type ChartGranularity, maxPointsForWidth } from '@/lib/helpers/performance-chart.helpers'
const props = withDefaults(
  defineProps<{
    labels: string[]
    current: number[]
    previous?: number[]
    previousLabels?: string[]
    valueFormatter?: (value: number) => string
    tooltipValueFormatter?: (value: number) => string
  }>(),
  {
    previous: () => [],
    previousLabels: () => [],
    valueFormatter: undefined,
    tooltipValueFormatter: undefined,
  },
)
const CURRENT_LABEL = 'Current'
const PREVIOUS_LABEL = 'Previous'
const AXIS_FORMATS: Record<ChartGranularity, string> = {
  day: 'MMM D',
  week: 'MMM D',
  month: 'MMM',
  quarter: '[Q]Q YY',
  year: 'YYYY',
}
const TOOLTIP_FORMATS: Record<ChartGranularity, string> = {
  day: 'MMM D',
  week: '[Week of] MMM D',
  month: 'MMMM YYYY',
  quarter: '[Q]Q YYYY',
  year: 'YYYY',
}
const container = ref<HTMLElement | null>(null)
const width = ref(0)
let observer: ResizeObserver | null = null
onMounted(() => {
  if (!container.value) {
    return
  }
  width.value = container.value.clientWidth
  observer = new ResizeObserver((entries) => {
    width.value = entries[0]?.contentRect.width ?? width.value
  })
  observer.observe(container.value)
})
onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
const maxPoints = computed(() => (width.value > 0 ? maxPointsForWidth(width.value) : Number.POSITIVE_INFINITY))
const aggregated = computed(() =>
  aggregatePerformanceChart(
    {
      labels: props.labels,
      current: props.current,
      previous: props.previous,
      previousLabels: props.previousLabels,
      previousRange: null,
    },
    maxPoints.value,
  ),
)
const currentHex = cssVar('--pink-500')
const blackHex = cssVar('--black')
const whiteHex = cssVar('--white')
const CURRENT_COLOR = currentHex
const PREVIOUS_COLOR = hexToRgba(blackHex, 0.16)
const sharedDatasetStyle = {
  tension: 0.4,
  borderWidth: 2,
  pointRadius: 0,
  pointHoverRadius: 5,
  pointHoverBorderWidth: 8,
  fill: 'origin' as const,
}
const hasData = computed(() => aggregated.value.current.length > 0)
const formatValue = (value: number) => (props.valueFormatter ? props.valueFormatter(value) : String(value))
const formatTooltipValue = (value: number) => (props.tooltipValueFormatter ? props.tooltipValueFormatter(value) : formatValue(value))
const buildFill = (color: string) => (context: ScriptableContext<'line'>) => {
  const { ctx, chartArea } = context.chart
  if (!chartArea) {
    return undefined
  }
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, color)
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
  return gradient
}
const axisLabels = computed(() => {
  const format = AXIS_FORMATS[aggregated.value.granularity]
  return aggregated.value.currentLabels.map((date) => moment(date).format(format))
})
const chartData = computed<ChartData<'line'>>(() => ({
  labels: axisLabels.value,
  datasets: [
    ...(aggregated.value.previous.length
      ? [
          {
            label: PREVIOUS_LABEL,
            data: aggregated.value.previous,
            borderColor: PREVIOUS_COLOR,
            pointBackgroundColor: PREVIOUS_COLOR,
            pointHoverBorderColor: hexToRgba(blackHex, 0.08),
            backgroundColor: buildFill(hexToRgba(blackHex, 0.08)),
            ...sharedDatasetStyle,
          },
        ]
      : []),
    {
      label: CURRENT_LABEL,
      data: aggregated.value.current,
      borderColor: CURRENT_COLOR,
      pointBackgroundColor: CURRENT_COLOR,
      pointHoverBorderColor: hexToRgba(currentHex, 0.4),
      backgroundColor: buildFill(hexToRgba(currentHex, 0.5)),
      ...sharedDatasetStyle,
    },
  ],
}))
const dateForItem = (item: TooltipItem<'line'>, granularity: ChartGranularity): string => {
  const labels = item.dataset.label === PREVIOUS_LABEL ? aggregated.value.previousLabels : aggregated.value.currentLabels
  const date = labels[item.dataIndex]
  return date ? moment(date).format(TOOLTIP_FORMATS[granularity]) : ''
}
const options = computed<ChartOptions<'line'>>(() => {
  const granularity = aggregated.value.granularity
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        type: 'category',
        grid: { color: 'rgba(0, 0, 0, 0.08)', drawTicks: false },
        border: { display: false },
        ticks: { maxRotation: 0, autoSkip: true, color: 'rgba(0, 0, 0, 0.32)', padding: 8 },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: 'rgba(0, 0, 0, 0.32)',
          padding: 8,
          callback: (value) => formatValue(Number(value)),
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: true,
        usePointStyle: true,
        cornerRadius: 16,
        padding: 12,
        backgroundColor: whiteHex,
        titleColor: blackHex,
        titleFont: { family: 'Urbanist', size: 14, weight: 600 },
        bodyColor: blackHex,
        bodyFont: { family: 'Urbanist', size: 14 },
        bodySpacing: 8,
        borderColor: 'rgba(60, 75, 93, 0.16)',
        borderWidth: 1,
        itemSort: (a: TooltipItem<'line'>, b: TooltipItem<'line'>) => b.datasetIndex - a.datasetIndex,
        filter: (item: TooltipItem<'line'>) => dateForItem(item, granularity) !== '',
        callbacks: {
          title: () => '',
          label: (item: TooltipItem<'line'>) =>
            ` ${item.dataset.label} · ${dateForItem(item, granularity)}: ${formatTooltipValue(Number(item.parsed.y))}`,
        },
      },
    },
  }
})
</script>
<template>
  <div ref="container" class="performance-line-chart">
    <LineChart v-if="hasData" :data="chartData" :options="options" />
    <div v-else class="performance-line-chart__empty">Sorry, we found no data matching your filters.</div>
  </div>
</template>
<style scoped lang="scss">
.performance-line-chart {
  width: 100%;
  height: 320px;
}
.performance-line-chart__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(0, 0, 0, 0.32);
}
</style>
